import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import {
  Switch,
  Route,
  useRouteMatch,
  Redirect,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { inject, observer } from 'mobx-react';

import AdminPage from './Admin';
import ComputationsPage from './ComputationsPage';
import ContactAdminPage from './ContactAdminPage';
import HelpCenter from './HelpCenter';
import NewComputationPage from './NewComputationPage';
import ModelNotFoundPage from './ModelNotFoundPage';
import ModelPage from './ModelPage';
import ModelsRoute from './ModelsRoute';
import { fetchTreeInsightsEnabled } from '../components/model/rgipChatApi';
import {
  ADMIN_PAGES_UNRESOLVED,
  fetchAdminPagesEnabled,
} from '../utils/adminSetupPages';
import { RootLayout } from '../components/layout/RootLayout';
import { useAppNavConfig } from '../components/layout/app-sidebar/nav-config';
import ViewEditComputationPage from './ViewEditComputationPage';
import BehavioralAggregationsPage from './BehavioralAggregationsPage';
import NewBehavioralAggregationPage from './NewBehavioralAggregationPage';
import ViewEditBehavioralAggregationPage from './ViewEditBehavioralAggregationPage';
import FrequencyAnalysisPage from './FrequencyAnalysisPage';
import { APP_NAME } from '../../utils/constants';
import getPageInfo, {
  getPageViewKey,
  PageInfo,
} from '../../utils/get-page-info';
import Store from '../../store';
import NewModelPage from './model/NewModel';
import HGLogo from '../assets/images/hg-logo-blue.svg';
import HGIcon from '../assets/images/hg-logo-only-blue.svg';
import { buildAdminUrl } from '../utils/adminUrl';
import { canSeeDataStudio, isV2User } from '../utils/rbac';
import { useGuides } from '../utils/guides';

type StudioPageProps = {
  store?: Store;
};

function StudioPage({ store }: StudioPageProps) {
  const paramsTenant = useParams<{ tenant: string }>().tenant;
  // Use tenant from params if available, otherwise fall back to store (for SSR compatibility)
  const tenant =
    paramsTenant || (store?.tenant ? String(store.tenant) : undefined);

  const match = useRouteMatch();
  const { pathname } = useLocation();
  const [pageName, setPageName] = useState<string>('');
  // The last page view sent, as getPageViewKey's key. Cleared on a path with
  // no page name, so coming back to a page after one sends a view again.
  const lastPageView = useRef<string | null>(null);
  // `studio-tree-insights`, read once per tenant for the sidebar. False until
  // it resolves and on any failure, so the nav starts as today's and gains the
  // Setup section rather than flashing it away.
  const [revamped, setRevamped] = useState(false);
  // Which generation of the admin app's audience and conversion screens this
  // tenant gets, for the Setup section's two links. Until it resolves those
  // point at the pages that need no flag, so the rows are never dead.
  const [adminPages, setAdminPages] = useState(ADMIN_PAGES_UNRESOLVED);

  useEffect(() => {
    if (!tenant) return undefined;
    let live = true;
    fetchTreeInsightsEnabled(tenant)
      .catch(() => false)
      .then((enabled) => {
        if (live) setRevamped(enabled);
      });
    fetchAdminPagesEnabled(tenant).then((answer) => {
      if (live) setAdminPages(answer);
    });
    return () => {
      live = false;
    };
  }, [tenant]);

  useGuides(
    store && {
      userId: store.userId,
      tenant: store.tenant,
      isInternalUser: store.isSuperKudu,
    }
  );

  useEffect(() => {
    // Read the location here rather than `pathname`: a <Redirect> mounted in
    // the same commit has already replaced it by the time this runs, so that
    // redirect sends one view, for where it lands. A redirect rendered later,
    // after a flag or data resolves (ModelDetailsPage, ReviewPage, ModelPage's
    // /deployment), still sends a view for the path it leaves.
    const pageInfo = getPageInfo(window.location.pathname);

    if (!pageInfo) {
      lastPageView.current = null;
      return;
    }

    const key = getPageViewKey(pageInfo);
    if (lastPageView.current !== key) {
      trackPageView(pageInfo);
      lastPageView.current = key;
    }
    setPageName(pageInfo.pageName);
  }, [pathname]);

  const trackPageView = ({ pageName: viewedPage, params }: PageInfo) => {
    try {
      if (!store?.email) {
        return; // Don't track unauthenticated users
      }

      // Event-scoped properties only. email/domain/tenant/role are user
      // traits and are sent via identify, not on each event (CPF-1199).
      // `page_name` because the `title` Segment records is the previous
      // page's: the <Helmet> title below only updates after this call.
      const properties: Record<string, any> = {
        app: 'studio',
        tenant: store.tenant,
        path: window.location.pathname,
        page_name: viewedPage,
      };

      // Add contextual parameters from URL
      Object.assign(properties, params);

      // Add model type if we're on a model page
      if (params.model_id) {
        const { activeModel } = store;
        properties.model_type = activeModel.type;
      }

      // Identify the user before tracking the page view
      store.analyticsIdentify();

      // Track page view with fixed "Studio" page name
      window.analytics.page('Studio', properties);
    } catch (e) {
      // avoid displaying analytics error for user
      // an error here means that the analytics tracking failed
    }
  };

  // Redirect user to first valid tenant if no permissions to access the current tenant
  // Only check authorization if we have both store and tenant
  if (!store || !tenant || !store.isUserAuthorizedForTenant(tenant)) {
    const redirectPath = store?.firstTenant
      ? `/tenant/${store.firstTenant}`
      : '/';
    console.log(
      `[StudioPage] User not authorized for tenant ${tenant}, redirecting to ${redirectPath}`
    );
    return <Redirect to={redirectPath} />;
  }

  // RBAC v2 Data Studio access (RGI-3158).
  //
  // Data Studio is a gated product: the tenant must have purchased it, then
  // the user must be an Org Admin or hold the module. Computations and
  // Aggregations live in Admin now, so there is no partial access any more.
  // v1 users (no rbac) and SuperKudu are unchanged.
  //
  // | Tenant bought it | Role          | data_studio | Result           |
  // | ---------------- | ------------- | ----------- | ---------------- |
  // | Yes              | Org Admin     | any         | Full access      |
  // | Yes              | Standard User | Yes         | Full access      |
  // | Yes              | Standard User | No          | ContactAdminPage |
  // | Yes              | Read-only     | -           | ContactAdminPage |
  // | No               | any           | any         | ContactAdminPage |
  // | -                | SuperKudu     | any         | Full access      |
  const rbac = store?.rbac;
  const superKudu = !!store?.isSuperKudu;
  if (isV2User(rbac) && !superKudu) {
    if (!canSeeDataStudio(rbac)) {
      return <ContactAdminPage moduleLabel="Data Studio" />;
    }
  }
  const showModelsRoute = canSeeDataStudio(rbac, { superKudu });

  const navConfig = useAppNavConfig({
    isSuperKudu: store?.isSuperKudu ?? false,
    tenant: tenant, // Pass tenant explicitly for SSR compatibility
    rbac: rbac ?? null,
    revamped,
    adminPages,
  });

  const tenantNumber = tenant ? Number(tenant) : null;
  // Use buildAdminUrl if tenant exists, otherwise use just the base URL
  const logoHref = tenantNumber
    ? buildAdminUrl(tenantNumber, '/')
    : NEW_ADMIN_BASE_URL;

  // Use the user's actual home tenant from permissions, not the URL tenant (store.tenant)
  const primaryTenantId =
    store?.userPermissions?.[0]?.tenant !== undefined &&
    store?.userPermissions?.[0]?.tenant !== null
      ? String(store.userPermissions[0].tenant)
      : undefined;

  return (
    <RootLayout
      navConfig={navConfig}
      logo={<img src={HGLogo} alt="HG Insights logo" />}
      collapsedLogo={<img src={HGIcon} alt="HG Insights logo" />}
      logoHref={logoHref}
      user={store?.email ? { email: store.email } : undefined}
      tenantId={tenant}
      isSuperKudu={store?.isSuperKudu ?? false}
      primaryTenantId={primaryTenantId}
      rbac={rbac ?? null}
    >
      <Helmet>
        <title>
          {APP_NAME} - {pageName}
        </title>
      </Helmet>
      <div
        style={{
          display: 'flex',
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'var(--app-canvas)',
          // The shell publishes this when the responsive layout is on. With it
          // off the property does not exist and the fallback is today's value,
          // so this line renders identically either way.
          padding: 'var(--shell-gutter, 24px)',
        }}
      >
        <Container fluid style={{ padding: 0 }}>
          <Switch>
            <Route path={`${match.path}/404`}>
              <ModelNotFoundPage />
            </Route>
            <Route path={`${match.path}/admin`}>
              <AdminPage />
            </Route>
            {showModelsRoute && (
              <Route path={`${match.path}/models/new`}>
                <NewModelPage />
              </Route>
            )}
            {showModelsRoute && (
              <Route path={`${match.path}/models/:modelId`}>
                <ModelPage />
              </Route>
            )}
            {showModelsRoute && (
              <Route path={`${match.path}/models`}>
                <ModelsRoute />
              </Route>
            )}
            <Route path={`${match.path}/computations/new`}>
              <NewComputationPage />
            </Route>
            <Route path={`${match.path}/computations/:computationName`}>
              <ViewEditComputationPage />
            </Route>
            <Route path={`${match.path}/computations`}>
              <ComputationsPage />
            </Route>
            <Route path={`${match.path}/helpCenter`}>
              <HelpCenter />
            </Route>
            <Route path={`${match.path}/aggregations/new`}>
              <NewBehavioralAggregationPage />
            </Route>
            <Route
              path={`${match.path}/aggregations/:aggregationName/frequency_analysis`}
            >
              <FrequencyAnalysisPage />
            </Route>
            <Route path={`${match.path}/aggregations/:aggregationName`}>
              <ViewEditBehavioralAggregationPage />
            </Route>
            <Route path={`${match.path}/aggregations`}>
              <BehavioralAggregationsPage />
            </Route>
            <Redirect
              from={match.path}
              to={`${match.path}/${
                showModelsRoute ? 'models' : 'computations'
              }`}
            />
          </Switch>
        </Container>
      </div>
    </RootLayout>
  );
}

export default inject('store')(observer(StudioPage));
